import { useCallback, useRef } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import type { RiskIqProps } from '@tri-opra/riskiq-web';
import { RiqStatusType, ProtocolDocumentStatus } from 'types/graphql-global-types';
import {
    acceptAndPromoteRiskIQAssociation,
    acceptAndPromoteRiskIQAssociationVariables,
    acceptRiskIQAssociation,
    acceptRiskIQAssociationVariables,
    confirmProtocolDocumentDecisions,
    confirmProtocolDocumentDecisionsVariables,
    createProtocolDocument,
    createProtocolDocumentVariables,
    pendingRiskIQAssociation,
    pendingRiskIQAssociationVariables,
    rejectRiskIQAssociation,
    rejectRiskIQAssociationVariables,
    updateProtocolDocumentStatus,
    updateProtocolDocumentStatusVariables,
} from '@queries/generated/protocol-insights.generated';
import {
    criticalVariableByRiqId,
    criticalVariableByRiqIdVariables,
    riskByRiqId,
    riskByRiqIdVariables,
    updateCriticalVariableRiqDetails,
    updateCriticalVariableRiqDetailsVariables,
    updateCriticalVariableRiqStatus,
    updateCriticalVariableRiqStatusVariables,
    updateRiskRiqDetails,
    updateRiskRiqDetailsVariables,
    updateRiskRiqStatus,
    updateRiskRiqStatusVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    ACCEPT_AND_PROMOTE_RISK_IQ_ASSOCIATION,
    ACCEPT_RISK_IQ_ASSOCIATION,
    ADD_PROTOCOL_DOCUMENT,
    CONFIRM_PROTOCOL_DOCUMENT_DECISIONS,
    PENDING_RISK_IQ_ASSOCIATION,
    REJECT_RISK_IQ_ASSOCIATION,
    UPDATE_PROTOCOL_DOCUMENT,
} from '@queries/protocol-insights';
import {
    CRITICAL_VARIABLE_BY_RIQ_ID,
    RISK_BY_RIQ_ID,
    UPDATE_CRITICALVARIABLE_RIQ_DETAILS,
    UPDATE_CRITICALVARIABLE_RIQ_STATUS,
    UPDATE_RISK_RIQ_DETAILS,
    UPDATE_RISK_RIQ_STATUS,
} from '@queries/risk-assessment';

/**
 * Maps RiskIQ component callbacks (`onAppEvent`) onto the equivalent OPRA GraphQL
 * mutations. The event union, entity ref and promoted-record shapes are owned by the
 * external `@tri-opra/riskiq-web` package, so they are DERIVED from `RiskIqProps` here
 * rather than redeclared — that way this handler tracks the package contract automatically.
 *
 * Several events do not yet carry all the data OPRA needs (see the TODOs below). Those are
 * wired as far as the current payloads allow and flagged for re-evaluation once the
 * component callbacks are updated to return the missing data.
 */
type RiskIqEventHandler = NonNullable<RiskIqProps['onAppEvent']>;
export type RiskIqEvent = Parameters<RiskIqEventHandler>[0];

type RiskIqEntityRef = Extract<RiskIqEvent, { type: 'item-accepted' }>['entity'];

// The CtQ/Risk pair the association mutations need. AB#90141 puts these on the item-accepted/
// item-rejected/item-accepted-and-promoted events and (per its follow-up) on each PromotedRecord —
// populated for association entities, null for ctq/risk.
type AssociationPair = { ctqId: string | null; riskId: string | null };

// The CtQ/Risk selected in a `selection-changed` event, for the caller to hold and feed to the
// comments component. The selected riqId is resolved to the OPRA CtQ/Risk Mongo id (the comments
// component keys off the Mongo id, not the riqId), and only the type + Mongo id are carried.
export type SelectedRiskIqItem = { type: 'ctq' | 'risk'; id: string };

const reportError =
    (context: string) =>
    (error: unknown): void => {
        // eslint-disable-next-line no-console
        console.error(`[RiskIQ] Failed to handle "${context}"`, error);
    };

export const useRiskIqEventHandler = (
    studyId: string | undefined,
    onSelectionChange?: (item: SelectedRiskIqItem | null) => void,
    onReviewStarted?: (isFinished: boolean) => void,
    onReviewClosed?: () => void,
    onSelectionChangesDiscarded?: () => void,
    onDecisionsConfirmed?: () => void,
): RiskIqEventHandler => {
    const [runUpdateCtqStatus] = useMutation<updateCriticalVariableRiqStatus, updateCriticalVariableRiqStatusVariables>(
        UPDATE_CRITICALVARIABLE_RIQ_STATUS,
    );
    const [runUpdateRiskStatus] = useMutation<updateRiskRiqStatus, updateRiskRiqStatusVariables>(
        UPDATE_RISK_RIQ_STATUS,
    );
    const [runUpdateCtqDetails] = useMutation<
        updateCriticalVariableRiqDetails,
        updateCriticalVariableRiqDetailsVariables
    >(UPDATE_CRITICALVARIABLE_RIQ_DETAILS);
    const [runUpdateRiskDetails] = useMutation<updateRiskRiqDetails, updateRiskRiqDetailsVariables>(
        UPDATE_RISK_RIQ_DETAILS,
    );
    const [runAcceptAssociation] = useMutation<acceptRiskIQAssociation, acceptRiskIQAssociationVariables>(
        ACCEPT_RISK_IQ_ASSOCIATION,
    );
    const [runRejectAssociation] = useMutation<rejectRiskIQAssociation, rejectRiskIQAssociationVariables>(
        REJECT_RISK_IQ_ASSOCIATION,
    );
    const [runAcceptAndPromoteAssociation] = useMutation<
        acceptAndPromoteRiskIQAssociation,
        acceptAndPromoteRiskIQAssociationVariables
    >(ACCEPT_AND_PROMOTE_RISK_IQ_ASSOCIATION);
    const [runPendingAssociation] = useMutation<pendingRiskIQAssociation, pendingRiskIQAssociationVariables>(
        PENDING_RISK_IQ_ASSOCIATION,
    );
    const [runAddProtocolDocument] = useMutation<createProtocolDocument, createProtocolDocumentVariables>(
        ADD_PROTOCOL_DOCUMENT,
    );
    const [runUpdateProtocolDocumentStatus] = useMutation<
        updateProtocolDocumentStatus,
        updateProtocolDocumentStatusVariables
    >(UPDATE_PROTOCOL_DOCUMENT);
    const [runConfirmProtocolDocumentDecisions] = useMutation<
        confirmProtocolDocumentDecisions,
        confirmProtocolDocumentDecisionsVariables
    >(CONFIRM_PROTOCOL_DOCUMENT_DECISIONS);
    const [fetchCtqByRiqId] = useLazyQuery<criticalVariableByRiqId, criticalVariableByRiqIdVariables>(
        CRITICAL_VARIABLE_BY_RIQ_ID,
    );
    const [fetchRiskByRiqId] = useLazyQuery<riskByRiqId, riskByRiqIdVariables>(RISK_BY_RIQ_ID);

    // "Latest selection wins": each selection-changed bumps this counter; an in-flight lookup only
    // applies its result if its captured id is still the current one, so a slow earlier response can
    // never overwrite a newer selection.
    const selectionRequestIdRef = useRef(0);

    return useCallback<RiskIqEventHandler>(
        (event) => {
            const requireStudyId = (context: string): string | null => {
                if (!studyId) {
                    reportError(context)(new Error('No study selected'));
                    return null;
                }
                return studyId;
            };

            const setAssociationDecision = (
                associationId: string,
                status: RiqStatusType,
                pair: AssociationPair | undefined,
            ): void => {
                const resolvedStudyId = requireStudyId('association-decision');
                if (!resolvedStudyId) return;

                // The association mutations are keyed by the CtQ + Risk riqIds, not the association id.
                // AB#90141 supplies both on the item-* events and on each PromotedRecord. A missing pair
                // is unexpected for an association and is skipped with a logged error.
                if (!pair?.ctqId || !pair?.riskId) {
                    reportError('association-decision')(
                        new Error(`Missing CtQ/Risk pair for association ${associationId}`),
                    );
                    return;
                }

                const variables = {
                    criticalVariableRiqId: pair.ctqId,
                    riskRiqId: pair.riskId,
                    studyId: resolvedStudyId,
                };

                if (status === RiqStatusType.REJECTED) {
                    runRejectAssociation({ variables }).catch(reportError('association-rejected'));
                } else if (status === RiqStatusType.ACCEPTED_AND_PROMOTED) {
                    runAcceptAndPromoteAssociation({ variables }).catch(
                        reportError('association-accepted-and-promoted'),
                    );
                } else if (status === RiqStatusType.PENDING) {
                    runPendingAssociation({ variables }).catch(reportError('association-pending'));
                } else {
                    runAcceptAssociation({ variables }).catch(reportError('association-accepted'));
                }
            };

            // Routes a CtQ / Risk / Association entity to the correct RIQ-status mutation. `rejection`
            // (code + reason) applies only to CtQ/Risk on rejection; `pair` (CtQ+Risk riqIds) applies
            // only to associations. Each is ignored by the other branch.
            const setEntityStatus = (
                entity: RiskIqEntityRef,
                status: RiqStatusType,
                options?: { rejection?: { code: string; reason: string }; pair?: AssociationPair },
            ): void => {
                const resolvedStudyId = requireStudyId('entity-status');
                if (!resolvedStudyId) return;

                switch (entity.type) {
                    case 'ctq':
                        runUpdateCtqStatus({
                            variables: {
                                riqId: entity.id,
                                status,
                                studyId: resolvedStudyId,
                                code: options?.rejection?.code,
                                reason: options?.rejection?.reason,
                            },
                        }).catch(reportError('ctq-status'));
                        break;
                    case 'risk':
                        runUpdateRiskStatus({
                            variables: {
                                riqId: entity.id,
                                status,
                                studyId: resolvedStudyId,
                                code: options?.rejection?.code,
                                reason: options?.rejection?.reason,
                            },
                        }).catch(reportError('risk-status'));
                        break;
                    case 'association':
                        setAssociationDecision(entity.id, status, options?.pair);
                        break;
                    default:
                        reportError('entity-status')(new Error(`Unknown entity type "${entity.type}"`));
                }
            };

            const setProtocolDocumentStatus = (protocolDocumentId: string, status: ProtocolDocumentStatus): void => {
                // TODO(RiskIQ hand-off): confirm `protocolDocumentId` is the OPRA ProtocolDocument
                // Guid (and not the RiskIQ-side id) before this reaches production.
                runUpdateProtocolDocumentStatus({
                    variables: { id: protocolDocumentId, status },
                }).catch(reportError('protocol-document-status'));
            };

            const process = (current: RiskIqEvent): void => {
                switch (current.type) {
                    // --- CtQ / Risk / Association status changes -----------------------------------
                    case 'item-accepted':
                        setEntityStatus(current.entity, RiqStatusType.ACCEPTED, {
                            pair: { ctqId: current.ctqId, riskId: current.riskId },
                        });
                        break;
                    case 'item-rejected':
                        // reasonCode + comment map to the CtQ/Risk status mutation's `code` + `reason`
                        // fields. (Association rejection has no code/reason arguments, so they are ignored
                        // for associations.)
                        setEntityStatus(current.entity, RiqStatusType.REJECTED, {
                            rejection: { code: current.reasonCode, reason: current.comment },
                            pair: { ctqId: current.ctqId, riskId: current.riskId },
                        });
                        break;
                    case 'item-accepted-and-promoted':
                        setEntityStatus(current.entity, RiqStatusType.ACCEPTED_AND_PROMOTED, {
                            pair: { ctqId: current.ctqId, riskId: current.riskId },
                        });
                        break;
                    case 'promoted':
                        // Intentionally no OPRA writes. RiskIQ fires this straight after `decisions-confirmed`
                        // (without waiting), and every record in it already had its Accept & Promote decision
                        // written by the earlier item-* event. confirmProtocolDocumentDecisions then moves them
                        // to Imported. Re-sending AcceptedAndPromoted here raced that transaction: a status
                        // write based on a pre-confirm read could land after it, reverting the risk/CtQ out of
                        // Imported and leaving an empty audit record.
                        break;

                    // --- Detail edits --------------------------------------------------------------
                    case 'risk-updated': {
                        const resolvedStudyId = requireStudyId('risk-updated');
                        if (!resolvedStudyId) break;
                        // AB#90065: `categoryId` is the resolved OPRA Risk Category list-item id (null on
                        // no match / String mode). It is stored in Mongo as an ObjectId (cast on save), so
                        // it goes to the mutation's `categoryId: ObjectId` arg; null when unmatched leaves
                        // it unset. The raw `category` display string is intentionally not sent.
                        // (source excerpt/page have no target field on this mutation.)
                        runUpdateRiskDetails({
                            variables: {
                                riqId: current.riskId,
                                studyId: resolvedStudyId,
                                categoryId: current.categoryId,
                                statement: current.statement,
                            },
                        }).catch(reportError('risk-updated'));
                        break;
                    }
                    case 'ctq-updated': {
                        const resolvedStudyId = requireStudyId('ctq-updated');
                        if (!resolvedStudyId) break;
                        // AB#90065: `ctqTypeId` is the resolved OPRA CtQ Type list-item id (null on no
                        // match / String mode). It is stored in Mongo as an ObjectId (cast on save), so it
                        // goes to the mutation's `typeId: ObjectId` arg; null when unmatched leaves it unset.
                        // The raw `ctqType` display string is intentionally not sent. (source excerpt/page
                        // have no target field on this mutation.)
                        runUpdateCtqDetails({
                            variables: {
                                riqId: current.ctqId,
                                studyId: resolvedStudyId,
                                typeId: current.ctqTypeId,
                                description: current.description,
                            },
                        }).catch(reportError('ctq-updated'));
                        break;
                    }

                    // --- Protocol document lifecycle ----------------------------------------------
                    case 'document-uploaded': {
                        const resolvedStudyId = requireStudyId('document-uploaded');
                        if (!resolvedStudyId) break;
                        runAddProtocolDocument({
                            variables: {
                                protocolDocument: {
                                    // documentId maps to the ProtocolDocument.riqId.
                                    riqId: current.documentId,
                                    documentName: current.documentName,
                                    version: current.version,
                                    status: ProtocolDocumentStatus.PROCESSING,
                                    studyId: resolvedStudyId,
                                },
                            },
                        }).catch(reportError('document-uploaded'));
                        break;
                    }
                    case 'review-started':
                        setProtocolDocumentStatus(current.protocolDocumentId, ProtocolDocumentStatus.IN_REVIEW);
                        onReviewStarted?.(current.isFinished);
                        break;
                    case 'review-discarded':
                        setProtocolDocumentStatus(current.protocolDocumentId, ProtocolDocumentStatus.DISCARDED);
                        break;
                    case 'review-closed':
                        onReviewClosed?.();
                        break;
                    case 'selection-changes-discarded':
                        onSelectionChangesDiscarded?.();
                        break;
                    case 'decisions-confirmed': {
                        onDecisionsConfirmed?.();

                        // confirmProtocolDocumentDecisions does the whole finalisation atomically on the
                        // backend: promotes the accepted-and-promoted RiskIQ risks/CtQs to Imported, copies
                        // their accepted-and-promoted associations into the live association lists, and sets
                        // the document status to ReviewCompleted. The event carries the RiskIQ document id
                        // (stored on the OPRA ProtocolDocument as riqId), which is exactly this mutation's
                        // `riqDocumentId` argument.
                        const resolvedStudyId = requireStudyId('decisions-confirmed');
                        if (!resolvedStudyId) break;
                        runConfirmProtocolDocumentDecisions({
                            variables: { riqDocumentId: current.protocolDocumentId, studyId: resolvedStudyId },
                        }).catch(reportError('decisions-confirmed'));
                        break;
                    }

                    // --- Bulk ----------------------------------------------------------------------
                    case 'bulk-action':
                        current.actions.forEach(process);
                        break;

                    // --- Selection ----------------------------------------------------------------
                    case 'selection-changed': {
                        const { selection } = current;
                        // Any new selection invalidates an in-flight lookup ("latest selection wins").
                        selectionRequestIdRef.current += 1;
                        const requestId = selectionRequestIdRef.current;
                        const isLatest = (): boolean => requestId === selectionRequestIdRef.current;

                        // Cleared selection, or an association selected (no CtQ/Risk comments panel).
                        if (!selection || selection.type === 'association') {
                            onSelectionChange?.(null);
                            break;
                        }
                        const resolvedStudyId = requireStudyId('selection-changed');
                        if (!resolvedStudyId) break;

                        // Resolve the selected riqId to the OPRA CtQ/Risk Mongo id and hand the type +
                        // Mongo id back to the caller (index.tsx) for the comments component — but only if
                        // it's still current. A missing record / id clears the selection.
                        const { type } = selection;
                        const riqId = selection.id;
                        if (type === 'ctq') {
                            fetchCtqByRiqId({ variables: { riqId, studyId: resolvedStudyId } })
                                .then((result) => {
                                    if (!isLatest()) return;
                                    const id = result.data?.criticalVariableByRiqId?.id ?? null;
                                    onSelectionChange?.(id ? { type, id } : null);
                                })
                                .catch(reportError('selection-changed'));
                        } else {
                            fetchRiskByRiqId({ variables: { riqId, studyId: resolvedStudyId } })
                                .then((result) => {
                                    if (!isLatest()) return;
                                    const id = result.data?.riskByRiqId?.id ?? null;
                                    onSelectionChange?.(id ? { type, id } : null);
                                })
                                .catch(reportError('selection-changed'));
                        }
                        break;
                    }
                    case 'association-created':
                        // A newly created association is registered in OPRA in PENDING state. The event
                        // carries the CtQ/Risk pair directly, so route it through the association-decision
                        // path (→ pendingRiskIQAssociation).
                        setAssociationDecision(current.associationId, RiqStatusType.PENDING, {
                            ctqId: current.ctqId,
                            riskId: current.riskId,
                        });
                        break;
                    // --- No OPRA action yet (stubs to fill in when needed) ------------------------
                    case 'association-updated':
                        // TODO(RiskIQ hand-off): AB#90066 added riskId/ctqId to this event (so the
                        // CtQ/Risk pair the association mutations need is now available), but it still
                        // carries no accept/reject/promote discriminator, so the target mutation cannot be
                        // chosen yet. Also type-level only for now — no live trigger to test against.
                        break;
                    case 'protocol-selected':
                        // TODO(RiskIQ hand-off): no OPRA action confirmed yet.
                        break;
                    case 'association-auto-rejected-cascade':
                        // Fired when a CtQ/Risk is rejected: the association can never be accepted (half of
                        // it is gone), so it is auto-rejected. Reject the association for the supplied pair.
                        setAssociationDecision(current.associationId, RiqStatusType.REJECTED, {
                            ctqId: current.ctqId,
                            riskId: current.riskId,
                        });
                        break;
                    default:
                        reportError('unhandled-event')(
                            new Error(`Unhandled RiskIQ event "${(current as RiskIqEvent).type}"`),
                        );
                }
            };

            process(event);
        },
        [
            studyId,
            runUpdateCtqStatus,
            runUpdateRiskStatus,
            runUpdateCtqDetails,
            runUpdateRiskDetails,
            runAcceptAssociation,
            runRejectAssociation,
            runAcceptAndPromoteAssociation,
            runPendingAssociation,
            runAddProtocolDocument,
            runUpdateProtocolDocumentStatus,
            runConfirmProtocolDocumentDecisions,
            fetchCtqByRiqId,
            fetchRiskByRiqId,
            onSelectionChange,
            onReviewStarted,
            onReviewClosed,
            onSelectionChangesDiscarded,
            onDecisionsConfirmed,
        ],
    );
};
